import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Pencil, Trash2, Plus, Save, X } from 'lucide-react';
import DataTable from '../components/DataTable';
import SearchBar from '../components/SearchBar';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import DateRangeFilter from '../components/DateRangeFilter';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';

// Manual PTO entries. Stored as Approved 'PTO' absences so the timecard calculation
// adds them to PERM employees' worked days (used to complete an employee's hours, e.g.
// reach 40, in emergencies or when waiting on a punch).
const EMPTY_FORM = { badge_num: '', id_emp_abs: '', start_date_abs: '', end_date_abs: '', pto_abs: '', reason: '' };

// Days covered by a range, both ends included.
function dayCount(from, to) {
  if (!from || !to) return 1;
  const a = new Date(from + 'T00:00:00');
  const b = new Date(to + 'T00:00:00');
  const n = Math.round((b - a) / 86400000) + 1;
  return n > 0 ? n : 1;
}

export default function PtoManual() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [empName, setEmpName] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/absences', { params: { employee: search, dateFrom, dateTo, type: 'PTO', page, limit } });
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('pto.fetchError') || 'Error loading PTO.' });
    }
    setLoading(false);
  }, [search, dateFrom, dateTo, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const lookupEmployee = async (badge) => {
    if (!badge) { setEmpName(''); setForm(f => ({ ...f, id_emp_abs: '' })); return; }
    try {
      const res = await axios.get(`/api/employees/badge/${badge}`);
      setEmpName(`${res.data.name_emp} ${res.data.last_name_emp}`);
      setForm(f => ({ ...f, id_emp_abs: res.data.id_emp }));
    } catch {
      setEmpName(t('abs.notFound'));
      setForm(f => ({ ...f, id_emp_abs: '' }));
    }
  };

  const searchEmployees = async (query) => {
    setEmpSearch(query);
    if (!query || query.length < 2) { setEmpResults([]); return; }
    try {
      const res = await axios.get('/api/employees', { params: { search: query } });
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setEmpResults(list.slice(0, 8));
    } catch { setEmpResults([]); }
  };

  const selectEmployee = (emp) => {
    setForm(f => ({ ...f, badge_num: String(emp.badge_num_emp), id_emp_abs: emp.id_emp }));
    setEmpName(`${emp.name_emp} ${emp.last_name_emp}`);
    setEmpSearch(`${emp.name_emp} ${emp.last_name_emp}`);
    setEmpResults([]);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEmpName('');
    setEmpSearch('');
    setEmpResults([]);
    setEditReason('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      badge_num: row.badge_num_emp || '',
      id_emp_abs: row.id_emp_abs,
      start_date_abs: row.start_date_abs ? row.start_date_abs.substring(0, 10) : '',
      end_date_abs: row.end_date_abs ? row.end_date_abs.substring(0, 10) : '',
      pto_abs: row.pto_abs != null ? Number(row.pto_abs) : '',
      reason: '',
    });
    setEmpName(row.name_abs ? `${row.name_abs} ${row.last_name_abs}` : '');
    setEmpSearch('');
    setEmpResults([]);
    setEditReason('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.id_emp_abs) { Swal.fire({ icon: 'warning', title: t('abs.invalidEmp'), text: t('abs.invalidEmpMsg') }); return; }
    if (!form.start_date_abs) { Swal.fire({ icon: 'warning', title: t('common.error'), text: t('pto.dateReq') || 'Date is required.' }); return; }
    if (form.end_date_abs && form.end_date_abs < form.start_date_abs) {
      Swal.fire({ icon: 'warning', title: t('common.error'), text: t('pto.rangeInvalid') || 'The end date cannot be before the start date.' });
      return;
    }
    if (!form.pto_abs || Number(form.pto_abs) <= 0) { Swal.fire({ icon: 'warning', title: t('common.error'), text: t('pto.hoursReq') || 'PTO hours are required.' }); return; }
    if (editing && !editReason.trim()) {
      Swal.fire({ icon: 'warning', title: t('audit.reasonRequired') || 'Razon requerida', text: t('audit.reasonRequiredMsg') || 'Indica el motivo del cambio.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id_emp_abs: form.id_emp_abs,
        start_date_abs: form.start_date_abs,
        // Empty "to" = a single day. The calculation applies pto_abs to EVERY day of the range, so
        // one entry covers a whole vacation instead of one row per day.
        end_date_abs: form.end_date_abs || form.start_date_abs,
        type_abs: 'PTO',
        status_abs: 'Approved',
        pto_abs: Number(form.pto_abs),
        reason: editing ? editReason : (form.reason || undefined),
      };
      if (editing) {
        await axios.put(`/api/absences/${editing.id_abs}`, payload);
      } else {
        await axios.post('/api/absences', payload);
      }
      setModalOpen(false);
      await fetchData();
      Swal.fire({ icon: 'success', title: editing ? (t('pto.updated') || 'PTO updated.') : (t('pto.created') || 'PTO created.'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setSaving(false);
  };

  const askDelete = async (id) => {
    const { value: reasonInput } = await Swal.fire({
      title: t('pto.deleteConfirm') || 'Eliminar PTO?',
      input: 'textarea',
      inputLabel: t('audit.reasonRequired') || 'Razon (obligatoria)',
      inputPlaceholder: t('audit.reasonPh') || 'Por que se esta eliminando?',
      inputValidator: (v) => !v?.trim() && (t('audit.reasonRequiredMsg') || 'La razon es obligatoria.'),
      showCancelButton: true,
      confirmButtonText: t('common.delete') || 'Eliminar',
      cancelButtonText: t('common.cancel') || 'Cancelar',
      confirmButtonColor: '#ef4444',
    });
    if (!reasonInput) return;
    setDeleteReason(reasonInput);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/absences/${deleteId}`, { data: { reason: deleteReason } });
      setDeleteId(null);
      setDeleteReason('');
      fetchData();
      Swal.fire({ icon: 'success', title: t('pto.deleted') || 'PTO deleted.', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.deleteError') });
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.substring(0, 10) : d;
    const dt = new Date(s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : ''));
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US');
  };

  const columns = [
    { header: t('common.badge'), accessor: 'badge_num_emp' },
    { header: t('abs.employee2'), render: (r) => r.name_abs ? `${r.name_abs} ${r.last_name_abs}` : '-' },
    {
      header: t('pto.dateCol') || 'Date',
      // One entry can now cover several days, so the column shows the range and how long it is.
      render: (r) => {
        const start = r.start_date_abs ? String(r.start_date_abs).substring(0, 10) : '';
        const end = r.end_date_abs ? String(r.end_date_abs).substring(0, 10) : start;
        if (!end || end === start) return formatDate(start);
        const n = dayCount(start, end);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatDate(start)} — {formatDate(end)}
            <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 12 }}>
              ({n} {t('pto.days') || 'days'})
            </span>
          </span>
        );
      },
    },
    {
      header: t('pto.hoursCol') || 'PTO hours',
      render: (r) => {
        const start = r.start_date_abs ? String(r.start_date_abs).substring(0, 10) : '';
        const end = r.end_date_abs ? String(r.end_date_abs).substring(0, 10) : start;
        const hrs = Number(r.pto_abs) || 0;
        const n = end && end !== start ? dayCount(start, end) : 1;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <span className="badge badge-info" style={{ fontSize: 12 }}>{hrs} h{n > 1 ? `/${t('pto.dayShort') || 'day'}` : ''}</span>
            {n > 1 && (
              <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 12 }}>= {hrs * n} h</span>
            )}
          </span>
        );
      },
    },
    { header: t('common.status'), render: (r) => <span className="badge badge-success">{r.status_abs || 'Approved'}</span> },
    {
      header: t('common.actions'),
      style: { width: 120 },
      render: (r) => (
        <div className="table-actions">
          <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}><Pencil size={14} style={{ marginRight: 4 }} />{t('common.edit')}</button>
          <button className="btn-danger btn-sm" onClick={() => askDelete(r.id_abs)}><Trash2 size={14} style={{ marginRight: 4 }} />{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('pto.title') || 'Manual PTO'}</h1>
        <div className="page-header-actions">
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px', whiteSpace: 'nowrap' }} onClick={openCreate}><Plus size={16} style={{ marginRight: 4, flexShrink: 0 }} />{t('common.newMasc') || t('common.newFem')}</button>
        </div>
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: -8, marginBottom: 16 }}>
        {t('pto.help') || 'Register PTO hours manually to complete an employee’s day (e.g. reach 40 hours) in emergencies or when waiting on a punch. Saved as an Approved PTO absence and added automatically when the week is calculated.'}
      </p>

      <div className="filters-bar">
        <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('abs.search')} />
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={(v) => { setDateFrom(v); setPage(1); }} onDateToChange={(v) => { setDateTo(v); setPage(1); }} onRefresh={() => { setDateFrom(''); setDateTo(''); setPage(1); }} />
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={data} loading={loading} emptyMessage={t('pto.empty') || 'No PTO entries found.'} />
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? (t('pto.editTitle') || 'Edit PTO') : (t('pto.newTitle') || 'New PTO')}>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {/* Employee search section */}
            <div style={{
              padding: 16, marginBottom: 16,
              background: 'var(--bg-tertiary)', borderRadius: 'var(--radius)',
              border: empName && empName !== t('abs.notFound') ? '2px solid var(--success)' : '1px solid var(--border)',
            }}>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>{t('abs.employee')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  placeholder={t('abs.employeeSearch')}
                  value={empSearch}
                  onChange={e => {
                    searchEmployees(e.target.value);
                    if (/^\d+$/.test(e.target.value)) {
                      setForm(f => ({ ...f, badge_num: e.target.value }));
                    }
                  }}
                  onBlur={() => {
                    if (/^\d+$/.test(empSearch) && !form.id_emp_abs) lookupEmployee(empSearch);
                    setTimeout(() => setEmpResults([]), 200);
                  }}
                  style={{ fontSize: 15 }}
                />
                {empResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  }}>
                    {empResults.map(emp => (
                      <div key={emp.id_emp}
                        onMouseDown={() => selectEmployee(emp)}
                        style={{
                          padding: '10px 14px', cursor: 'pointer',
                          borderBottom: '1px solid var(--border)',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontWeight: 600 }}>{emp.name_emp} {emp.last_name_emp}</span>
                        <span className="badge badge-info" style={{ fontSize: 11 }}>Badge {emp.badge_num_emp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {empName && empName !== t('abs.notFound') && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="badge badge-success" style={{ fontSize: 12, padding: '4px 10px' }}>Badge {form.badge_num}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{empName}</span>
                </div>
              )}
              {empName === t('abs.notFound') && (
                <small style={{ color: 'var(--danger)', marginTop: 6, display: 'block' }}>{t('abs.notFound')}</small>
              )}
            </div>

            {/* Date range + PTO hours per day */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('pto.dateFrom') || 'From'} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <DateInput className="form-input" required value={form.start_date_abs}
                  onChange={v => setForm({ ...form, start_date_abs: v })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('pto.dateTo') || 'To'}</label>
                <DateInput className="form-input" value={form.end_date_abs} min={form.start_date_abs}
                  onChange={v => setForm({ ...form, end_date_abs: v })} />
              </div>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 12.5, marginTop: -8, marginBottom: 16 }}>
              {t('pto.rangeHint')}
            </p>

            <div className="form-row">
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">{t('pto.hoursPerDay') || 'PTO hours per day'} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input type="number" min="0" step="0.5" className="form-input" value={form.pto_abs}
                  onChange={e => setForm({ ...form, pto_abs: e.target.value })}
                  placeholder={t('ea.ptoPh') || 'e.g. 8'} />
                {/* What the entry adds up to, so a range is not confused with a total. */}
                {form.start_date_abs && form.end_date_abs && form.end_date_abs > form.start_date_abs && Number(form.pto_abs) > 0 && (
                  <small style={{ color: 'var(--text-secondary)', marginTop: 6, display: 'block' }}>
                    {dayCount(form.start_date_abs, form.end_date_abs)} {t('pto.days') || 'days'} ×{' '}
                    {Number(form.pto_abs)} h = <strong>{dayCount(form.start_date_abs, form.end_date_abs) * Number(form.pto_abs)} h</strong>
                  </small>
                )}
              </div>
            </div>

            {!editing && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label className="form-label">{t('ea.note') || 'Note'}</label>
                  <input className="form-input" value={form.reason}
                    onChange={e => setForm({ ...form, reason: e.target.value })}
                    placeholder={t('pto.notePh') || 'e.g. emergency, completed to 40'} />
                </div>
              </div>
            )}

            {editing && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label className="form-label">
                    {t('audit.reason') || 'Razon'} <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={editReason}
                    onChange={e => setEditReason(e.target.value)}
                    placeholder={t('audit.reasonPh') || 'Por que se esta editando?'}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} disabled={saving || !form.id_emp_abs}>
              <Save size={15} style={{ marginRight: 4 }} />{saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message={t('pto.deleteConfirm') || 'Eliminar PTO?'}
      />
    </div>
  );
}
